import React, { useState, useEffect } from 'react';
import { Play, ChevronDown, Tag, CreditCard, Check, X, AlertCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { packagesAPI, paymentsAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';

const Credits = () => {
  const { user, refreshUser } = useAuth();
  const [packages, setPackages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [transactionId, setTransactionId] = useState('');
  const [promoCode, setPromoCode] = useState('');
  const [promoDiscount, setPromoDiscount] = useState(null);
  const [promoError, setPromoError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  
  const [banglaVideoOpen, setBanglaVideoOpen] = useState(false);
  const [englishVideoOpen, setEnglishVideoOpen] = useState(false);

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      const response = await packagesAPI.getAll();
      setPackages(response.data);
    } catch (error) {
      console.error('Failed to fetch packages:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectPackage = (pkg) => {
    setSelectedPackage(pkg);
    setCheckoutOpen(true);
    setTransactionId('');
    setPromoCode('');
    setPromoDiscount(null);
    setPromoError('');
    setSubmitSuccess(false);
  };

  const handleApplyPromo = async () => {
    if (!promoCode.trim()) return;
    
    setPromoError('');
    try {
      const response = await paymentsAPI.validatePromo(promoCode, selectedPackage.id);
      setPromoDiscount(response.data);
    } catch (error) {
      setPromoError(error.response?.data?.detail || 'Invalid promo code');
      setPromoDiscount(null);
    }
  };

  const handleSubmitPayment = async () => {
    if (!transactionId.trim()) {
      alert('Please enter your transaction ID');
      return;
    }

    setIsSubmitting(true);
    try {
      await paymentsAPI.create({
        package_id: selectedPackage.id,
        transaction_id: transactionId,
        payment_method: 'offline',
        promo_code: promoDiscount ? promoCode : null,
      });
      setSubmitSuccess(true);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to submit payment');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getFinalPrice = () => {
    if (promoDiscount) {
      return promoDiscount.final_price;
    }
    return selectedPackage?.price_usd || 0;
  };

  const getFinalPriceBDT = () => {
    if (!selectedPackage) return 0;
    const ratio = selectedPackage.price_bdt / selectedPackage.price_usd;
    return getFinalPrice() * ratio;
  };

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold text-white">Buy Credits</h1>

      {/* Current Balance */}
      <div className="bg-gradient-to-r from-purple-600 to-purple-800 rounded-xl p-6 flex justify-between items-center">
        <div>
          <p className="text-purple-200 text-sm mb-1">Current Balance</p>
          <p className="text-white text-4xl font-bold">{(user?.credits || 0).toFixed(2)} credits</p>
        </div>
        <div className="text-right">
          <p className="text-purple-200 text-sm mb-1">Subscription</p>
          <p className="text-white text-xl font-bold">{user?.package || 'Free'}</p>
        </div>
      </div>

      {/* Pricing Packages */}
      <div>
        <h2 className="text-xl font-bold text-white mb-4">Choose a Package</h2>
        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {packages.map((pkg) => (
              <div
                key={pkg.id}
                className="bg-[#1a1a2e] rounded-xl p-6 border border-gray-800 hover:border-purple-500/50 transition-all text-center"
              >
                <h3 className="text-white font-semibold text-lg mb-3">{pkg.name}</h3>
                <p className="text-purple-400 text-3xl font-bold mb-1">
                  ${pkg.price_usd.toFixed(2)}
                </p>
                <p className="text-orange-400 text-sm mb-3">
                  ৳{pkg.price_bdt.toFixed(2)}
                </p>
                <p className="text-white text-lg mb-4">
                  <span className="text-2xl font-bold">{pkg.credits}</span>
                  <span className="text-gray-400 ml-1">credits</span>
                </p>
                
                {pkg.features && (
                  <div className="space-y-2 mb-4 text-left">
                    {pkg.features.map((feature, index) => (
                      <p key={index} className="text-gray-400 text-sm flex items-center gap-2">
                        <Check size={14} className="text-green-500" />
                        {feature}
                      </p>
                    ))}
                  </div>
                )}
                
                <Button
                  onClick={() => handleSelectPackage(pkg)}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                >
                  Purchase
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Tutorial Section */}
      <div className="rounded-xl overflow-hidden mt-8">
        <div className="bg-gradient-to-br from-purple-800 via-purple-700 to-orange-600 p-8 text-center">
          <h2 className="text-2xl font-bold text-white mb-2">How to Purchase Credits</h2>
          <p className="text-gray-200 mb-6">
            Watch our tutorial video to learn how to purchase credits using offline payment.
          </p>

          <div className="flex flex-wrap justify-center gap-4">
            <Collapsible open={banglaVideoOpen} onOpenChange={setBanglaVideoOpen}>
              <CollapsibleTrigger asChild>
                <Button
                  variant="outline"
                  className="bg-transparent border border-gray-500 text-white hover:bg-white/10 px-6"
                >
                  <Play size={16} className="mr-2" />
                  Watch Tutorial (Bangla)
                  <ChevronDown
                    size={16}
                    className={`ml-2 transition-transform ${banglaVideoOpen ? 'rotate-180' : ''}`}
                  />
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-2">
                <div className="bg-black/50 rounded-lg p-4">
                  <div className="aspect-video bg-gray-800 rounded-lg">
                    <iframe
                      width="100%"
                      height="100%"
                      src="https://www.youtube.com/embed/dQw4w9WgXcQ"
                      title="Tutorial Bangla"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="rounded-lg"
                    ></iframe>
                  </div>
                </div>
              </CollapsibleContent>
            </Collapsible>

            <Collapsible open={englishVideoOpen} onOpenChange={setEnglishVideoOpen}>
              <CollapsibleTrigger asChild>
                <Button
                  variant="outline"
                  className="bg-transparent border border-gray-500 text-white hover:bg-white/10 px-6"
                >
                  <Play size={16} className="mr-2" />
                  Watch Tutorial (English)
                  <ChevronDown
                    size={16}
                    className={`ml-2 transition-transform ${englishVideoOpen ? 'rotate-180' : ''}`}
                  />
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-2">
                <div className="bg-black/50 rounded-lg p-4">
                  <div className="aspect-video bg-gray-800 rounded-lg">
                    <iframe
                      width="100%"
                      height="100%"
                      src="https://www.youtube.com/embed/dQw4w9WgXcQ"
                      title="Tutorial English"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="rounded-lg"
                    ></iframe>
                  </div>
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
        </div>
      </div>

      {/* Checkout Modal */}
      <Dialog open={checkoutOpen} onOpenChange={setCheckoutOpen}>
        <DialogContent className="bg-[#1a1a2e] border-gray-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <CreditCard size={20} />
              Checkout - {selectedPackage?.name}
            </DialogTitle>
          </DialogHeader>

          {submitSuccess ? (
            <div className="py-8 text-center">
              <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
                <Check size={32} className="text-green-500" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Payment Submitted!</h3>
              <p className="text-gray-400 mb-4">
                Your payment is pending verification. Once verified by admin, your credits will be activated.
              </p>
              <Button onClick={() => setCheckoutOpen(false)} className="bg-purple-600 hover:bg-purple-700">
                Close
              </Button>
            </div>
          ) : (
            <>
              <div className="space-y-4 py-4">
                {/* Order Summary */}
                <div className="bg-[#12121a] rounded-lg p-4 border border-gray-700">
                  <h4 className="text-gray-400 text-sm mb-2">Order Summary</h4>
                  <div className="flex justify-between text-white mb-1">
                    <span>{selectedPackage?.name} Package</span>
                    <span>${selectedPackage?.price_usd.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-gray-400 text-sm mb-1">
                    <span>Credits</span>
                    <span>{selectedPackage?.credits}</span>
                  </div>
                  {promoDiscount && (
                    <div className="flex justify-between text-green-400 text-sm">
                      <span>Discount ({promoCode})</span>
                      <span>-${promoDiscount.discount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="border-t border-gray-700 mt-2 pt-2">
                    <div className="flex justify-between text-white font-bold">
                      <span>Total</span>
                      <span>${getFinalPrice().toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-orange-400 text-sm">
                      <span>In BDT</span>
                      <span>৳{getFinalPriceBDT().toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                {/* Promo Code */}
                <div className="space-y-2">
                  <Label className="text-gray-400">Promo Code (Optional)</Label>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Tag className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" size={16} />
                      <Input
                        value={promoCode}
                        onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                        placeholder="Enter promo code"
                        className="pl-10 bg-[#12121a] border-gray-700 text-white uppercase"
                      />
                    </div>
                    <Button
                      type="button"
                      onClick={handleApplyPromo}
                      variant="outline"
                      className="border-gray-700 text-gray-300"
                    >
                      Apply
                    </Button>
                  </div>
                  {promoError && (
                    <p className="text-red-400 text-sm flex items-center gap-1">
                      <X size={14} /> {promoError}
                    </p>
                  )}
                  {promoDiscount && (
                    <p className="text-green-400 text-sm flex items-center gap-1">
                      <Check size={14} /> Promo applied! You save ${promoDiscount.discount.toFixed(2)}
                    </p>
                  )}
                </div>

                {/* Payment Instructions */}
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <AlertCircle size={18} className="text-yellow-500 mt-0.5" />
                    <div>
                      <p className="text-yellow-400 font-medium text-sm">Offline Payment Instructions</p>
                      <p className="text-gray-400 text-sm mt-1">
                        Send ৳{getFinalPriceBDT().toFixed(2)} to bKash/Nagad: <span className="text-white">01XXXXXXXXX</span>
                      </p>
                      <p className="text-gray-400 text-sm">Reference: Your Email</p>
                    </div>
                  </div>
                </div>

                {/* Transaction ID */}
                <div className="space-y-2">
                  <Label className="text-gray-400">Transaction ID *</Label>
                  <Input
                    value={transactionId}
                    onChange={(e) => setTransactionId(e.target.value)}
                    placeholder="Enter your transaction ID"
                    className="bg-[#12121a] border-gray-700 text-white"
                    required
                  />
                  <p className="text-gray-500 text-xs">Enter the transaction ID from your bKash/Nagad payment</p>
                </div>
              </div>

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setCheckoutOpen(false)}
                  className="border-gray-700 text-gray-400"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmitPayment}
                  disabled={isSubmitting || !transactionId.trim()}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  {isSubmitting ? (
                    <span className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Submitting...
                    </span>
                  ) : (
                    'Submit Payment'
                  )}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Credits;
